import PaginatedResponse from "common/contracts/paginated-response";
import AHBoolean from "common/values/boolean/boolean";
import Date from "common/values/date/date";
import Guid from "common/values/guid/guid";
import Percent from "common/values/percent/percent";
import Session from "users/session/session";
import EntityClientRepresentative, {
  EntityClientRepresentativeJSON
} from "work/entities/entity-client-representative/entity-client-representative";
import EntityVendorRepresentative, {
  EntityVendorRepresentativeJSON
} from "work/entities/entity-vendor-representative/entity-vendor-representative";
import Proposal from "work/entities/proposal/proposal";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import { WorkAgreementQuery } from "work/entities/work-agreement/store/work-agreement-redux-slice";
import FeeScheduleCategory, {
  FeeScheduleCategoryJSON,
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import FeeSchedule from "work/values/fee-schedule/fee-schedule";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import WorkDocument, { WorkDocumentJSON } from "work/values/work-document/work-document";
import ClientTeam, { ClientTeamJSON } from "../../values/client-team/client-team";
import DetailedVendorTeam, { DetailedVendorTeamJSON } from "../../values/vendor-team/detailed-vendor-team";
import VendorTeam, { VendorTeamJSON } from "../../values/vendor-team/vendor-team";

export type MaskedType<T> = T | Masked | null | undefined;
export type MaskedField = typeof Masked.MaskedField;
export type MaskableTeam = VendorTeam | DetailedVendorTeam | MaskedTeam | undefined;

export interface WorkAgreementJSON {
  id?: string;
  RFPId?: string;
  name?: string;
  description?: string;
  client?: EntityClientRepresentativeJSON;
  clientTeam?: ClientTeamJSON | MaskedField;
  vendors: EntityVendorRepresentativeJSON[];
  team?: VendorTeamJSON | DetailedVendorTeamJSON | MaskedField;
  teamRestricted: boolean | MaskedField;
  feeSchedule?: FeeScheduleCategoryJSON[] | MaskedField;
  startDate?: string;
  endDate?: string;
  discount?: number | MaskedField;

  clientPolicyDocuments: WorkDocumentJSON[] | MaskedField;
  vendorPolicyDocuments: WorkDocumentJSON[] | MaskedField;
  conflictsCheckWaived: boolean | MaskedField;
  conflictsDocuments: WorkDocumentJSON[] | MaskedField;

  clientTeamTemplateIds: string[];
  vendorTeamTemplateIds: string[];
  clientFeeScheduleTemplateIds: string[];
  vendorFeeScheduleTemplateIds: string[];

  hireDate?: string;
  archiveDate?: string;
}

export default class WorkAgreement {
  private readonly _store: IWorkAgreementStore;
  id?: Guid;
  RFPId?: Guid;
  name?: ProjectName;
  description?: ProjectDescription;
  client?: EntityClientRepresentative;
  clientTeam?: ClientTeam | MaskedTeam;
  vendors: EntityVendorRepresentative[] = [];
  private _team?: VendorTeam | DetailedVendorTeam | MaskedTeam;
  teamRestricted: AHBoolean | MaskedTeam = new AHBoolean(false);
  feeSchedule?: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule = [];
  startDate?: Date;
  endDate?: Date;
  discount?: Percent | MaskedDiscount;

  clientPolicyDocuments: WorkDocument[] | MaskedContracts = [];
  vendorPolicyDocuments: WorkDocument[] | MaskedContracts = [];
  conflictsCheckWaived: AHBoolean | MaskedContracts = new AHBoolean(false);
  conflictsDocuments: WorkDocument[] | MaskedContracts = [];

  clientTeamTemplateIds: Guid[] = [];
  vendorTeamTemplateIds: Guid[] = [];
  clientFeeScheduleTemplateIds: Guid[] = [];
  vendorFeeScheduleTemplateIds: Guid[] = [];

  hireDate?: Date;
  archiveDate?: Date;

  constructor(
    store: IWorkAgreementStore
  ) {
    this._store = store;
  }

  get team(): MaskableTeam {
    return this._team;
  }

  set team(team: MaskableTeam) {
    this._team = team;
  }

  public get isArchived(): boolean {
    return this.archiveDate !== undefined;
  }

  public isAssociatedWithUserId(userId?: Guid): boolean {
    if (Masked.isMasked(this.team)) return false;
    if (!userId) return false;
    if (this.client?.userId.isEqualTo(userId)) return true;
    if (this.team?.leader?.userId.isEqualTo(userId)) return true;
    return !!this.team?.members.some((member) => member.userId.isEqualTo(userId));

  }

  public async reviseAgreement(session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!session.user?.id) {
      throw new Error("Cannot revise a work agreement without a user id.");
    }
    if (!this._store) {
      throw new Error("Store not initialized.");
    }
    return await this._store.reviseWorkAgreementAsync(this, session);
  }

  public async endAgreement(userId?: Guid): Promise<WorkAgreement> {
    if (!userId) {
      throw new Error("Cannot end a work agreement without a user id.");
    }
    if (!this._store) {
      throw new Error("Store not initialized.");
    }
    if (
      !this.client?.userId.isEqualTo(userId) &&
      !this.vendors.some(vendor => vendor.userId.isEqualTo(userId))
    ) {
      throw new Error("You are not permitted to end this work agreement.");
    }

    return await this._store.endWorkAgreementAsync(this);
  }

  public async quitWorkTeam(userId: Guid, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!userId) {
      throw new Error("Cannot quit a work vendor-team without a user id.");
    }
    if (!this._store) {
      throw new Error("Store not initialized.");
    }
    if (!this.team) {
      throw new Error("No vendor-team to quit from.");
    }

    if (Masked.isMasked(this.team)) {
      throw new Error("You are not permitted to quit from a masked vendor-team.");
    }
    if (!this.team.members.some(member => member.userId.isEqualTo(userId))) {
      throw new Error("You are not a member of this vendor-team.");
    }
    return await this._store.quitWorkTeamAsync(this.id!, session);
  }


  clone(): WorkAgreement {
    let clonedWorkAgreement = new WorkAgreement(this._store);

    clonedWorkAgreement.id = this.id?.clone();
    clonedWorkAgreement.RFPId = this.RFPId?.clone();
    clonedWorkAgreement.name = this.name?.clone();
    clonedWorkAgreement.description = this.description?.clone();
    clonedWorkAgreement.client = this.client?.clone();
    clonedWorkAgreement.clientTeam = this.clientTeam?.clone();
    clonedWorkAgreement.vendors = this.vendors.map(vendor => vendor.clone());
    clonedWorkAgreement.team = this.team?.clone();
    clonedWorkAgreement.teamRestricted = this.teamRestricted?.clone();
    clonedWorkAgreement.feeSchedule =
      Masked.isMasked(this.feeSchedule) ? this.feeSchedule :
        this.feeSchedule?.map(feeSchedule => feeSchedule.clone());
    clonedWorkAgreement.startDate = this.startDate?.clone();
    clonedWorkAgreement.endDate = this.endDate?.clone();
    clonedWorkAgreement.discount = this.discount?.clone();

    clonedWorkAgreement.clientPolicyDocuments =
      Masked.isMasked(this.clientPolicyDocuments) ? this.clientPolicyDocuments :
        this.clientPolicyDocuments.map(doc => doc.clone());
    clonedWorkAgreement.vendorPolicyDocuments =
      Masked.isMasked(this.vendorPolicyDocuments) ? this.vendorPolicyDocuments :
        this.vendorPolicyDocuments.map(doc => doc.clone());
    clonedWorkAgreement.conflictsDocuments =
      Masked.isMasked(this.conflictsDocuments) ? this.clientPolicyDocuments :
        this.conflictsDocuments.map(doc => doc.clone());
    clonedWorkAgreement.conflictsCheckWaived = this.conflictsCheckWaived.clone();

    clonedWorkAgreement.clientTeamTemplateIds = this.clientTeamTemplateIds.map(id => id.clone());
    clonedWorkAgreement.vendorTeamTemplateIds = this.vendorTeamTemplateIds.map(id => id.clone());
    clonedWorkAgreement.clientFeeScheduleTemplateIds = this.clientFeeScheduleTemplateIds.map(id => id.clone());
    clonedWorkAgreement.vendorFeeScheduleTemplateIds = this.vendorFeeScheduleTemplateIds.map(id => id.clone());

    clonedWorkAgreement.hireDate = this.hireDate?.clone();
    clonedWorkAgreement.archiveDate = this.archiveDate?.clone();

    return clonedWorkAgreement;
  }

  public toJSON(): WorkAgreementJSON {
    return {
      id: this.id?.toJSON(),
      RFPId: this.RFPId?.toJSON(),
      name: this.name?.toJSON(),
      description: this.description?.toJSON(),
      client: this.client?.toJSON(),
      clientTeam: this.clientTeam?.toJSON(),
      vendors: this.vendors.map(vendor => vendor.toJSON()),
      team: this.team?.toJSON(),
      teamRestricted: this.teamRestricted.toJSON(),
      feeSchedule:
        Masked.isMasked(this.feeSchedule) ? this.feeSchedule.toJSON() :
          this.feeSchedule?.map(feeSchedule => feeSchedule.toJSON()),
      startDate: this.startDate?.toJSON(),
      endDate: this.endDate?.toJSON(),
      discount: this.discount?.toJSON(),
      clientPolicyDocuments:
        Masked.isMasked(this.clientPolicyDocuments) ? this.clientPolicyDocuments.toJSON() :
          this.clientPolicyDocuments.map(doc => doc.toJSON()),
      vendorPolicyDocuments:
        Masked.isMasked(this.vendorPolicyDocuments) ? this.vendorPolicyDocuments.toJSON() :
          this.vendorPolicyDocuments.map(doc => doc.toJSON()),
      conflictsCheckWaived: this.conflictsCheckWaived.toJSON(),
      conflictsDocuments:
        Masked.isMasked(this.conflictsDocuments) ? this.conflictsDocuments.toJSON() :
          this.conflictsDocuments.map(doc => doc.toJSON()),
      clientTeamTemplateIds: this.clientTeamTemplateIds.map(id => id.toJSON()),
      vendorTeamTemplateIds: this.vendorTeamTemplateIds.map(id => id.toJSON()),
      clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds.map(id => id.toJSON()),
      vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds.map(id => id.toJSON()),

      hireDate: this.hireDate?.toJSON(),
      archiveDate: this.archiveDate?.toJSON(),
    };
  }

  public static fromJSON(store: IWorkAgreementStore, json: WorkAgreementJSON): WorkAgreement {
    let team: MaskableTeam = undefined;
    if (Masked.isMasked(json.team)) {
      team = new MaskedTeam();
    } else if (json.team) {
      team = 'members' in json.team ? DetailedVendorTeam.fromJSON(json.team) : VendorTeam.fromJSON(json.team);
    }
    const workAgreement = new WorkAgreement(store);
    workAgreement.id = json.id ? Guid.fromJSON(json.id) : undefined;
    workAgreement.RFPId = json.RFPId ? Guid.fromJSON(json.RFPId) : undefined;
    workAgreement.name = json.name ? ProjectName.fromJSON(json.name) : undefined;
    workAgreement.description = json.description ? ProjectDescription.fromJSON(json.description) : undefined;
    workAgreement.client = json.client ? EntityClientRepresentative.fromJSON(json.client) : undefined;
    workAgreement.clientTeam = typeof json.clientTeam === 'object' && 'isMasked' in json.clientTeam ? new MaskedTeam() : ClientTeam.fromJSON(json.clientTeam);
    workAgreement.vendors = json.vendors.map((vendor) => EntityVendorRepresentative.fromJSON(vendor));
    workAgreement.team = team;
    workAgreement.teamRestricted = typeof json.teamRestricted === "object" && 'isMasked' in json.teamRestricted ? new MaskedTeam() : AHBoolean.fromJSON(json.teamRestricted);
    workAgreement.feeSchedule = typeof json.feeSchedule === 'object' && 'isMasked' in json.feeSchedule ? new MaskedFeeSchedule() :
      json.feeSchedule?.map((feeSchedule) => FeeScheduleCategory.fromJSON(feeSchedule));
    workAgreement.startDate = Date.fromJSON(json.startDate) ?? undefined;
    workAgreement.endDate = Date.fromJSON(json.endDate) ?? undefined;
    workAgreement.discount = typeof json.discount === 'object' && 'isMasked' in json.discount ? new MaskedDiscount() : Percent.fromJSON(json.discount);
    workAgreement.clientPolicyDocuments = typeof json.clientPolicyDocuments === 'object' && 'isMasked' in json.clientPolicyDocuments ? new MaskedContracts() :
      json.clientPolicyDocuments.map((doc) => WorkDocument.fromJSON(doc));
    workAgreement.vendorPolicyDocuments = typeof json.vendorPolicyDocuments === 'object' && 'isMasked' in json.vendorPolicyDocuments ? new MaskedContracts() :
      json.vendorPolicyDocuments.map((doc) => WorkDocument.fromJSON(doc));
    workAgreement.conflictsCheckWaived = typeof json.conflictsCheckWaived === 'object' && 'isMasked' in json.conflictsCheckWaived ? new MaskedContracts() :
      AHBoolean.fromJSON(json.conflictsCheckWaived);
    workAgreement.conflictsDocuments = typeof json.conflictsDocuments === 'object' && 'isMasked' in json.conflictsDocuments ? new MaskedContracts() :
      json.conflictsDocuments.map((doc) => WorkDocument.fromJSON(doc));

    workAgreement.clientTeamTemplateIds = json.clientTeamTemplateIds.map((id) => Guid.fromJSON(id));
    workAgreement.vendorTeamTemplateIds = json.vendorTeamTemplateIds.map((id) => Guid.fromJSON(id));
    workAgreement.clientFeeScheduleTemplateIds = json.clientFeeScheduleTemplateIds.map((id) => Guid.fromJSON(id));
    workAgreement.vendorFeeScheduleTemplateIds = json.vendorFeeScheduleTemplateIds.map((id) => Guid.fromJSON(id));

    workAgreement.hireDate = Date.fromJSON(json.hireDate) ?? undefined;
    workAgreement.archiveDate = Date.fromJSON(json.archiveDate) ?? undefined;

    return workAgreement;
  }
}

export interface IWorkAgreementStore {
  getWorkAgreementsAsync(query: WorkAgreementQuery, abortController?: AbortController): Promise<PaginatedResponse<WorkAgreement>>;
  getWorkAgreementByIdAsync(workId: Guid, abortController?: AbortController): Promise<WorkAgreement>;
  getWorkTeamsAsync(workId: Guid, abortController?: AbortController): Promise<VendorTeam[]>;
  reviseWorkAgreementAsync(workAgreement: WorkAgreement, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;
  endWorkAgreementAsync(workAgreement: WorkAgreement): Promise<WorkAgreement>;
  quitWorkTeamAsync(workId: Guid, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;
  getWorkFeeSchedulesAsync(entityId: Guid, representativeType?: string, abortController?: AbortController): Promise<FeeSchedule[]>;
}

export enum WorkAgreementFieldCategory {
  Details = "Details",
  Team = "Team",
  FeeSchedule = "Fee Schedule",
  Conflicts = "Conflicts",
  Policies = "Policies",
  Discount = "Discount",
  Invoices = "Invoices"
}

export abstract class Masked {
  public static readonly MaskedField = { isMasked: true };

  public clone = () => this;

  public toJSON = () => (Masked.MaskedField);

  private static isMaskedJSON(value: unknown): value is typeof Masked.MaskedField {
    return value != null && typeof value === "object" && 'isMasked' in value && value.isMasked === true;
  }

  public static isMasked<V>(value: V): value is Extract<V, Masked | typeof Masked.MaskedField> {
    return value instanceof Masked || Masked.isMaskedJSON(value);
  }

  public static isNotMasked<V>(value: V): value is Exclude<V, Masked | typeof Masked.MaskedField> {
    return !(value instanceof Masked) && !Masked.isMaskedJSON(value);
  }

  public static isPresentNotMasked<V>(value: V): value is Exclude<NonNullable<V>, Masked | typeof Masked.MaskedField> {
    return value != null && !(value instanceof Masked) && !Masked.isMaskedJSON(value);
  }

  public static assertMasked<V>(value: V): asserts value is Extract<V, Masked | typeof Masked.MaskedField> {
    if (!(value instanceof Masked) && !Masked.isMaskedJSON(value)) {
      throw new Error("Value is not masked");
    }
  }

  public static assertNotMasked<V>(value: V): asserts value is Exclude<V, Masked | typeof Masked.MaskedField> {
    if (value instanceof Masked || Masked.isMaskedJSON(value)) {
      throw new Error("Value is masked");
    }
  }

  public static assertPresentNotMasked<V>(value: V): asserts value is Exclude<NonNullable<V>, Masked> {
    if (value == null || value instanceof Masked || Masked.isMaskedJSON(value)) {
      throw new Error("Value is null/undefined or masked");
    }
  }

  public static isEqual<T extends { isEqualTo(other: T): boolean }>(
    a: MaskedType<T>,
    b: MaskedType<T>
  ): boolean {
    // Only compare when both sides are present and not masked
    if (Masked.isPresentNotMasked(a) && Masked.isPresentNotMasked(b)) {
      return a.isEqualTo(b);
    }
    // If either side is masked (or missing), treat as equal
    // so it doesn't cause inequality
    return true;
  }

  public static isOptionalEqual<T extends { isEqualTo(other: T): boolean }>(
    a: MaskedType<T>,
    b: MaskedType<T>
  ): boolean {
    // If both are present and not masked, compare real values
    if (Masked.isPresentNotMasked(a) && Masked.isPresentNotMasked(b)) {
      return a.isEqualTo(b);
    }
    // If exactly one side is present and not masked, they differ
    if (Masked.isPresentNotMasked(a) !== Masked.isPresentNotMasked(b)) {
      return false;
    }
    // Otherwise (both absent, or at least one masked), treat as equal/ignored
    return true;
  }

  public static isUnorderedArrayEqual<T extends { isEqualTo(other: T): boolean }>(
    a: T[] | Masked | undefined,
    b: T[] | Masked | undefined
  ): boolean {
    // If either is masked, ignore differences
    if (Masked.isMasked(a) || Masked.isMasked(b)) return true;

    // Now both sides are either arrays or undefined
    if (!a || !b) return (!a && !b);

    if (a.length !== b.length) return false;
    // Unordered: every a has a match in b and vice versa
    return a.every(ax => b.some(bx => bx.isEqualTo(ax)))
      && b.every(bx => a.some(ax => ax.isEqualTo(bx)));
  }
}
export class MaskedTeam extends Masked { }
export class MaskedFeeSchedule extends Masked { }
export class MaskedContracts extends Masked { }
export class MaskedDiscount extends Masked { }
